import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { ReactSortable } from 'react-sortablejs'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Popover from '@radix-ui/react-popover'
import {
  ArrowLeft,
  Bell,
  BellOff,
  ChevronDown,
  ChevronRight,
  Flame,
  GripVertical,
  Heart,
  MoreHorizontal,
  MessageSquare,
  MessagesSquare,
  Plus,
  RefreshCw,
  Repeat2,
  Search,
  Settings,
  SlidersHorizontal,
  Star,
  Tag,
  Trash2,
  UserPlus,
  X
} from 'lucide-react'
import { api } from '../lib/api'
import type { Actor, BoardColumn, BoardColumnColor, Keyword, Post, PostListFilters, Project, ProjectMember } from '../lib/types'
import { formatCount, formatDateTime, formatRelative } from '../lib/format'
import { Link, navigate } from '../lib/router'
import { realtime, type RealtimeEvent } from '../lib/realtime'
import { errorText, useToast } from '../components/Toaster'
import { useConfirm } from '../components/Confirm'
import { Avatar, Badge, Button, Field, IconButton, Input, Modal, Notice, Select, Spinner, Textarea } from '../ds'
import { TemplatesSettings } from '../components/TemplatesSettings'
import { GenerationWorkspace } from '../components/GenerationWorkspace'
import { useProjectNav, type ProjectTab } from '../lib/projectNav'
import { PostView } from './PostView'

// Для SortableJS у элемента списка обязателен строковый id (у Post он всегда есть).
type BoardPost = Post & { id: string }
type Columns = Record<string, BoardPost[]>

// Режимы сортировки колонок. 'manual' — порядок, заданный перетаскиванием (boardOrder);
// остальные сортируют по дате/популярности на клиенте (посты уже загружены, лимит 200).
type SortKey = 'manual' | 'date_desc' | 'date_asc' | 'pop_desc' | 'pop_asc'

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'manual', label: 'Вручную' },
  { value: 'date_desc', label: 'Сначала новые' },
  { value: 'date_asc', label: 'Сначала старые' },
  { value: 'pop_desc', label: 'Сначала популярные' },
  { value: 'pop_asc', label: 'Сначала непопулярные' }
]

// Период доски — независимый фильтр по дате публикации. Применяется server-side
// (через `from` в listPosts → publishedAt >= from), поэтому доска не тянет лишнее
// даже при большой истории. days = 0 → без ограничения.
type PeriodKey = 'all' | '24h' | '3d' | '7d' | '30d' | '90d' | '365d'
const DAY_MS = 86_400_000
const PERIOD_OPTIONS: { value: PeriodKey; label: string; days: number }[] = [
  { value: 'all', label: 'За всё время', days: 0 },
  { value: '24h', label: 'За 24 часа', days: 1 },
  { value: '3d', label: 'За 3 дня', days: 3 },
  { value: '7d', label: 'За неделю', days: 7 },
  { value: '30d', label: 'За месяц', days: 30 },
  { value: '90d', label: 'За квартал', days: 90 },
  { value: '365d', label: 'За год', days: 365 }
]

// ISO-граница «from» для выбранного периода (или undefined — без ограничения).
function periodFromIso(period: PeriodKey): string | undefined {
  const days = PERIOD_OPTIONS.find((p) => p.value === period)?.days ?? 0
  return days ? new Date(Date.now() - days * DAY_MS).toISOString() : undefined
}

// Человекочитаемые названия языков (ISO 639-1) для фильтра. Неизвестный код —
// показываем как есть в верхнем регистре; 'und' — не определён.
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'Английский',
  ru: 'Русский',
  uk: 'Украинский',
  de: 'Немецкий',
  es: 'Испанский',
  fr: 'Французский',
  it: 'Итальянский',
  pt: 'Португальский',
  pl: 'Польский',
  tr: 'Турецкий',
  nl: 'Нидерландский',
  sv: 'Шведский',
  ar: 'Арабский',
  zh: 'Китайский',
  ja: 'Японский',
  ko: 'Корейский',
  mn: 'Монгольский',
  id: 'Индонезийский',
  bg: 'Болгарский',
  be: 'Белорусский',
  kk: 'Казахский',
  tt: 'Татарский',
  hi: 'Хинди',
  vi: 'Вьетнамский',
  ro: 'Румынский',
  sr: 'Сербский',
  und: 'Не определён'
}
function languageLabel(code: string): string {
  return LANGUAGE_NAMES[code] || code.toUpperCase()
}

function projectTabFromUrl(): ProjectTab {
  const tab = new URLSearchParams(window.location.search).get('tab')
  return tab === 'generation' ? 'generation' : 'search'
}

// Сколько карточек грузим в колонку за раз (первая страница и каждое «Загрузить ещё»).
// Сортировка и фильтры — server-side; каждая колонка тянется отдельным запросом со
// своим limit/offset, поэтому колонки не голодают из-за общего лимита.
const PAGE_SIZE = 25

const BOARD_COLUMN_COLORS: { value: BoardColumnColor; label: string }[] = [
  { value: 'gray', label: 'Серый' },
  { value: 'blue', label: 'Синий' },
  { value: 'amber', label: 'Янтарный' },
  { value: 'green', label: 'Зелёный' },
  { value: 'red', label: 'Красный' },
  { value: 'teal', label: 'Бирюзовый' },
  { value: 'purple', label: 'Фиолетовый' },
  { value: 'pink', label: 'Розовый' },
  { value: 'brown', label: 'Коричневый' },
  { value: 'olive', label: 'Оливковый' }
]

function emptyColumns(): Columns {
  return {}
}

function columnsShape(boardColumns: BoardColumn[]): Columns {
  return Object.fromEntries(boardColumns.map((column) => [column.id, []]))
}

function countsShape(boardColumns: BoardColumn[]): Record<string, number> {
  return Object.fromEntries(boardColumns.map((column) => [column.id, 0]))
}

function sameColumnOrder(a: BoardColumn[], b: BoardColumn[]): boolean {
  return a.length === b.length && a.every((column, index) => column.id === b[index]?.id)
}

export function ProjectView({
  projectId,
  actor,
  peekPostId
}: {
  projectId: string
  actor: Actor
  peekPostId?: string | null
}) {
  const { notify } = useToast()
  const confirm = useConfirm()
  const [project, setProject] = useState<Project | null>(null)
  const [keywords, setKeywords] = useState<Keyword[]>([])
  const [members, setMembers] = useState<ProjectMember[]>([])
  const [boardColumns, setBoardColumns] = useState<BoardColumn[]>([])
  // Каждая колонка — независимый список (items) + общее число постов в статусе (total).
  // colReady — был ли первый запрос (чтобы показать спиннер до первой загрузки).
  const [columns, setColumns] = useState<Columns>(emptyColumns)
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [loadingCols, setLoadingCols] = useState<Set<string>>(new Set())
  const [colReady, setColReady] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const [activeTab, setActiveTab] = useState<ProjectTab>(() => projectTabFromUrl())
  const { setNav } = useProjectNav()
  const [editingColumn, setEditingColumn] = useState<BoardColumn | null>(null)
  const [deletingColumn, setDeletingColumn] = useState<BoardColumn | null>(null)

  // Фильтры доски
  const [keywordId, setKeywordId] = useState('')
  const [assigneeFilter, setAssigneeFilter] = useState('')
  const [query, setQuery] = useState('')
  const [favoriteOnly, setFavoriteOnly] = useState(false)
  const [sort, setSort] = useState<SortKey>('date_desc')
  const [period, setPeriod] = useState<PeriodKey>('all')
  const [languageFilter, setLanguageFilter] = useState('')
  const [languages, setLanguages] = useState<{ code: string; count: number }[]>([])

  // Массовый выбор карточек (общий по всей доске). Панель действий живёт в тулбаре.
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const [notifyBusy, setNotifyBusy] = useState(false)

  // Снимок колонок для realtime-обработчиков — чтобы читать актуальное состояние
  // без устаревших замыканий. boardOpsRef хранит свежие операции доски (см. ниже).
  const columnsRef = useRef<Columns>({})
  columnsRef.current = columns
  const boardOpsRef = useRef<{
    applyPostUpdate: (post: Post) => void
    removePost: (postId: string) => void
    reorderColumn: (columnId: string, orderedIds: string[]) => void
  } | null>(null)

  // Личный мьют email-уведомлений по этому проекту (флаг на моей membership).
  async function toggleProjectNotifications() {
    if (!project) return
    const next = !project.emailNotifications
    setNotifyBusy(true)
    setProject({ ...project, emailNotifications: next })
    try {
      const res = await api.updateProjectNotifications(projectId, next)
      setProject(res.project)
      notify(next ? 'Уведомления по проекту включены' : 'Уведомления по проекту выключены')
    } catch (err) {
      setProject({ ...project, emailNotifications: !next })
      notify(errorText(err), 'error')
    } finally {
      setNotifyBusy(false)
    }
  }

  function loadMeta() {
    Promise.all([api.getProject(projectId), api.listKeywords(projectId), api.listProjectMembers(projectId), api.listBoardColumns(projectId)])
      .then(([p, k, m, c]) => {
        setProject(p.project)
        setKeywords(k.keywords)
        setMembers(m.members)
        setBoardColumns(c.columns.filter((column) => Boolean(column.id)))
      })
      .catch((err) => setLoadError(errorText(err)))
    api
      .listProjectLanguages(projectId)
      .then((res) => setLanguages(res.languages))
      .catch(() => setLanguages([]))
  }

  // Общие фильтры доски (без статуса/пагинации) — одинаковы для всех колонок.
  function boardFilters(): PostListFilters {
    const filters: PostListFilters = { sort }
    if (keywordId) filters.keywordId = keywordId
    if (assigneeFilter) filters.assignedToEmail = assigneeFilter
    if (query.trim()) filters.q = query.trim()
    if (favoriteOnly) filters.favorite = true
    if (languageFilter) filters.language = languageFilter
    const from = periodFromIso(period)
    if (from) filters.from = from
    return filters
  }

  // Грузим одну колонку: reset=true — первая страница (заменяет), иначе — дозагрузка
  // следующей страницы (добавляет в конец).
  function loadColumn(columnId: string, reset: boolean) {
    setLoadingCols((prev) => new Set(prev).add(columnId))
    const offset = reset ? 0 : (columns[columnId]?.length ?? 0)
    api
      .listPosts(projectId, { ...boardFilters(), columnId, limit: PAGE_SIZE, offset })
      .then((res) => {
        const fresh = res.posts as BoardPost[]
        setColumns((prev) => ({ ...prev, [columnId]: reset ? fresh : [...(prev[columnId] || []), ...fresh] }))
        setTotals((prev) => ({ ...prev, [columnId]: res.pagination?.total ?? fresh.length }))
      })
      .catch((err) => setLoadError(errorText(err)))
      .finally(() => {
        setLoadingCols((prev) => {
          const next = new Set(prev)
          next.delete(columnId)
          return next
        })
      })
  }

  // Перезагрузка всей доски с первой страницы каждой колонки (смена фильтров/сортировки).
  function reloadBoard() {
    setColumns(columnsShape(boardColumns))
    setTotals(countsShape(boardColumns))
    boardColumns.forEach((column) => loadColumn(column.id, true))
    setColReady(true)
  }

  useEffect(loadMeta, [projectId])
  useEffect(() => {
    const sync = () => setActiveTab(projectTabFromUrl())
    window.addEventListener('popstate', sync)
    window.addEventListener('mrktng:navigate', sync)
    return () => {
      window.removeEventListener('popstate', sync)
      window.removeEventListener('mrktng:navigate', sync)
    }
  }, [])
  // Регистрируем вкладки раздела в глобальном Topbar (на линии лого/email).
  useEffect(() => {
    setNav({ activeTab, onSelect: selectTab })
    return () => setNav(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])
  // Сигнатура НАБОРА колонок (id без учёта порядка). Доску перезагружаем при
  // добавлении/удалении колонки или смене фильтров — но НЕ при простом reorder:
  // порядок чисто визуальный (рендерим по boardColumns), посты не меняются.
  const columnIdsKey = boardColumns
    .map((column) => column.id)
    .slice()
    .sort()
    .join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (boardColumns.length === 0) {
      setColReady(true)
      return
    }
    reloadBoard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, columnIdsKey, keywordId, assigneeFilter, favoriteOnly, period, languageFilter, sort])

  // Realtime: живые обновления доски от других участников проекта. Подписываемся
  // на комнату проекта; сервер шлёт события после записи. Эхо собственных действий
  // безопасно — применяется идемпотентно (тот же merge/порядок).
  useEffect(() => {
    realtime.subscribe(projectId)
    const off = realtime.on((event: RealtimeEvent) => {
      const ops = boardOpsRef.current
      if (!ops) return
      if (event.type === 'post:updated' && event.post) {
        ops.applyPostUpdate(event.post as Post)
      } else if (event.type === 'post:deleted' && typeof event.postId === 'string') {
        ops.removePost(event.postId)
      } else if (event.type === 'posts:reordered' && typeof event.columnId === 'string' && Array.isArray(event.orderedIds)) {
        ops.reorderColumn(event.columnId, event.orderedIds as string[])
      }
    })
    return () => {
      realtime.unsubscribe(projectId)
      off()
    }
  }, [projectId])

  // Чистим выбор от карточек, исчезнувших из текущих колонок.
  useEffect(() => {
    const live = new Set(Object.values(columns).flatMap((items) => items.map((p) => p.id)))
    setSelectedCards((prev) => {
      if (prev.size === 0) return prev
      const next = new Set([...prev].filter((id) => live.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [columns])

  function toggleCard(id: string) {
    setSelectedCards((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function setColumnSelection(ids: string[], on: boolean) {
    setSelectedCards((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  const clearCardSelection = () => setSelectedCards(new Set())

  const cardAssigneeOptions = [
    { value: '', label: 'Не назначен' },
    ...members.filter((m) => m.status === 'active').map((m) => ({ value: m.email, label: m.email }))
  ]

  // Опции фильтра по языку: значимые языки (≥5 постов отсеивают «хвост» промахов
  // детектора на коротком тексте) + всегда оставляем уже выбранный язык.
  const languageOptions = languages
    .filter((l) => l.count >= 5 || l.code === languageFilter)
    .map((l) => ({ value: l.code, label: `${languageLabel(l.code)} (${l.count})` }))

  // Число активных фильтров — для бейджа на кнопке «Фильтры».
  const activeFilters =
    (assigneeFilter ? 1 : 0) + (languageFilter ? 1 : 0) + (period !== 'all' ? 1 : 0) + (favoriteOnly ? 1 : 0)

  function resetFilters() {
    setAssigneeFilter('')
    setLanguageFilter('')
    setPeriod('all')
    setFavoriteOnly(false)
  }

  // Массовая операция над выбранными карточками: применяем, снимаем выбор, обновляем доску.
  async function runCardBulk(op: (id: string) => Promise<unknown>, doneMsg: string) {
    const ids = [...selectedCards]
    if (ids.length === 0) return
    setBulkBusy(true)
    try {
      await Promise.all(ids.map(op))
      notify(doneMsg)
      clearCardSelection()
      reloadBoard()
    } catch (err) {
      notify(errorText(err), 'error')
    } finally {
      setBulkBusy(false)
    }
  }

  const bulkCardColumn = (columnId: string) =>
    runCardBulk((id) => api.updatePost(id, { columnId }), 'Колонка обновлена')

  const bulkCardAssignee = (email: string) =>
    runCardBulk((id) => api.updatePost(id, { assignedToEmail: email || null }), 'Исполнитель обновлён')

  async function bulkCardDelete() {
    if (selectedCards.size === 0) return
    const ok = await confirm({
      title: 'Удалить выбранные карточки?',
      message: `Будет удалено: ${selectedCards.size}. Это действие необратимо.`
    })
    if (!ok) return
    await runCardBulk((id) => api.deletePost(id), 'Карточки удалены')
  }

  function refreshAll() {
    loadMeta()
    reloadBoard()
  }

  // Точечное обновление одной карточки (после правок в peek-панели): меняем поля
  // на месте, а при смене колонки — переносим карточку между списками. Без полной
  // перезагрузки доски, чтобы ничего не «скакало».
  function applyPostUpdate(updated: Post) {
    const id = updated.id
    if (!id) return
    const fromCol = Object.keys(columns).find((colId) => (columns[colId] ?? []).some((p) => p.id === id))
    if (!fromCol) return // карточки нет в текущей выборке — трогать нечего
    const toCol = updated.columnId || fromCol
    if (toCol === fromCol) {
      setColumns((prev) => ({
        ...prev,
        [fromCol]: (prev[fromCol] ?? []).map((p) => (p.id === id ? { ...p, ...updated, id } : p))
      }))
      return
    }
    // переезд в другую колонку
    setColumns((prev) => {
      const current = (prev[fromCol] ?? []).find((p) => p.id === id)
      const next: Columns = { ...prev, [fromCol]: (prev[fromCol] ?? []).filter((p) => p.id !== id) }
      if (current && prev[toCol]) {
        next[toCol] = [{ ...current, ...updated, id }, ...prev[toCol]!]
      }
      return next
    })
    setTotals((prev) => ({
      ...prev,
      [fromCol]: Math.max(0, (prev[fromCol] ?? 0) - 1),
      [toCol]: (prev[toCol] ?? 0) + 1
    }))
  }

  // Realtime: пост удалён другим участником — убираем карточку и правим счётчик.
  function removePostFromBoard(postId: string) {
    const cols = columnsRef.current
    const col = Object.keys(cols).find((colId) => (cols[colId] ?? []).some((p) => p.id === postId))
    if (!col) return
    setColumns((prev) => ({ ...prev, [col]: (prev[col] ?? []).filter((p) => p.id !== postId) }))
    setTotals((prev) => ({ ...prev, [col]: Math.max(0, (prev[col] ?? 0) - 1) }))
  }

  // Realtime: другой участник переупорядочил колонку — выстраиваем загруженные
  // карточки в присланном порядке (неподгруженные id просто игнорируем).
  function reorderColumnFromBoard(columnId: string, orderedIds: string[]) {
    const cols = columnsRef.current
    const current = cols[columnId]
    if (!current) return
    const byId = new Map(current.map((p) => [p.id, p]))
    const ordered = orderedIds.map((id) => byId.get(id)).filter((p): p is BoardPost => Boolean(p))
    const orderedSet = new Set(orderedIds)
    const rest = current.filter((p) => !orderedSet.has(p.id))
    setColumns((prev) => ({ ...prev, [columnId]: [...ordered, ...rest] }))
  }

  // Свежие операции доски для realtime-эффекта (эффект подписан один раз на projectId).
  boardOpsRef.current = {
    applyPostUpdate,
    removePost: removePostFromBoard,
    reorderColumn: reorderColumnFromBoard
  }

  // SortableJS двигает DOM и зовёт setList на обеих колонках — синхронизируем состояние.
  function setColumn(columnId: string, list: BoardPost[]) {
    setColumns((prev) => ({ ...prev, [columnId]: list }))
  }

  // Ручной режим: сохраняем порядок колонки-приёмника (id → boardOrder=index, плюс статус).
  async function persistColumn(columnId: string, orderedIds: string[]) {
    try {
      await api.reorderPosts(projectId, columnId, orderedIds)
    } catch (err) {
      notify(errorText(err), 'error')
      reloadBoard()
    }
  }

  // Режим сортировки: при переносе между колонками меняется только статус карточки,
  // boardOrder не трогаем (ручной порядок должен жить независимо от вида сортировки).
  async function persistPostColumn(postId: string, columnId: string) {
    try {
      await api.updatePost(postId, { columnId })
    } catch (err) {
      notify(errorText(err), 'error')
      reloadBoard()
    }
  }

  // SortableJS зовёт setList на колонках при перетаскивании. Поведение зависит от режима.
  // total колонки правим по дельте длины (карточка пришла/ушла при межколоночном переносе).
  function handleColumnChange(columnId: string, nextList: BoardPost[]) {
    const delta = nextList.length - (columns[columnId]?.length ?? 0)
    if (delta !== 0) {
      setTotals((t) => ({ ...t, [columnId]: Math.max(0, (t[columnId] ?? 0) + delta) }))
    }
    if (sort === 'manual') {
      setColumn(columnId, nextList)
      persistColumn(
        columnId,
        nextList.map((p) => p.id)
      )
      return
    }
    // В режиме сортировки переупорядочивание внутри колонки выключено (sort=false),
    // setList срабатывает только на межколоночный перенос. Находим «пришедшие» карточки
    // (их статус ещё прежний), фиксируем им новый статус. Точную позицию по серверной
    // сортировке не вычисляем — карточка встаёт туда, куда брошена (до перезагрузки).
    const movedIn = nextList.filter((p) => p.columnId !== columnId)
    setColumn(
      columnId,
      nextList.map((p) => (p.columnId !== columnId ? { ...p, columnId } : p))
    )
    movedIn.forEach((p) => persistPostColumn(p.id, columnId))
  }

  function handleColumnsChange(nextColumns: BoardColumn[]) {
    if (sameColumnOrder(boardColumns, nextColumns)) {
      return
    }
    setBoardColumns(nextColumns)
    api
      .reorderBoardColumns(
        projectId,
        nextColumns.map((column) => column.id)
      )
      .catch((err) => {
        notify(errorText(err), 'error')
        loadMeta()
      })
  }

  async function addColumn(name: string) {
    try {
      await api.createBoardColumn(projectId, { name })
      notify('Колонка добавлена')
      loadMeta()
    } catch (err) {
      notify(errorText(err), 'error')
      throw err
    }
  }

  async function saveColumnSettings(columnId: string, input: { name: string; color: BoardColumnColor }) {
    try {
      await api.updateBoardColumn(columnId, input)
      notify('Колонка обновлена')
      setEditingColumn(null)
      loadMeta()
    } catch (err) {
      notify(errorText(err), 'error')
      throw err
    }
  }

  async function deleteColumn(columnId: string, moveToColumnId?: string) {
    try {
      await api.deleteBoardColumn(columnId, { moveToColumnId })
      notify('Колонка удалена')
      setDeletingColumn(null)
      loadMeta()
    } catch (err) {
      notify(errorText(err), 'error')
      throw err
    }
  }

  if (loadError) {
    return (
      <div className="page">
        <Link href="/" className="back-link">
          <ArrowLeft size={14} /> К проектам
        </Link>
        <Notice>{loadError}</Notice>
      </div>
    )
  }

  if (!project || !colReady) {
    return (
      <div className="loading-screen">
        <Spinner />
      </div>
    )
  }

  const totalInView = boardColumns.reduce((sum, column) => sum + (totals[column.id] ?? 0), 0)
  const loadedInView = boardColumns.reduce((sum, column) => sum + (columns[column.id]?.length ?? 0), 0)

  // Ключевик, по которому отобран пост: id → запрос. Слово могли удалить (keywordId
  // сбрасывается через $unset) — тогда чипа просто не будет.
  const keywordById = new Map(keywords.map((k) => [k.id, k.query]))

  function selectTab(tab: ProjectTab) {
    setActiveTab(tab)
    navigate(`/projects/${projectId}${tab === 'search' ? '' : `?tab=${tab}`}`)
  }

  return (
    <div className="proj-layout">
      {activeTab === 'generation' ? (
        <GenerationWorkspace
          projectId={projectId}
          projectName={project.name}
          onOpenSettings={() => setShowSettings(true)}
        />
      ) : (
        <>
      <aside className="proj-sidebar">
        <div className="proj-side-head">
          <Link href="/" className="back-link">
            <ArrowLeft size={14} /> К проектам
          </Link>
          <div className="proj-side-title">
            <h1>{project.name}</h1>
            <IconButton
              label={project.emailNotifications ? 'Уведомления включены — выключить' : 'Уведомления выключены — включить'}
              onClick={toggleProjectNotifications}
              disabled={notifyBusy}
            >
              {project.emailNotifications ? <Bell size={16} /> : <BellOff size={16} />}
            </IconButton>
            <IconButton label="Настройки проекта" onClick={() => setShowSettings(true)}>
              <Settings size={16} />
            </IconButton>
          </div>
          <div className="faint" style={{ fontSize: 'var(--fs-12)' }}>
            {keywords.length} ключей · {totalInView} постов в выборке
            {totalInView > loadedInView ? ` (загружено ${loadedInView})` : ''}
          </div>
        </div>

        <SidebarKeywords
          projectId={projectId}
          keywords={keywords}
          activeKeywordId={keywordId}
          onFilter={(id) => setKeywordId((prev) => (prev === id ? '' : id))}
          onChanged={loadMeta}
          onSearched={(kw, found, created) => {
            notify(`«${kw.query}»: найдено ${found}, новых ${created}`)
            refreshAll()
          }}
          onSearchedAll={(found, created, failed) => {
            notify(
              `Поиск по всем словам: найдено ${found}, новых ${created}${failed ? `, ошибок ${failed}` : ''}`,
              failed ? 'error' : undefined
            )
            refreshAll()
          }}
          onError={(msg) => notify(msg, 'error')}
        />
      </aside>

      <main className="proj-main">
        <div className="board-toolbar">
          {selectedCards.size > 0 ? (
            // В режиме выбора подменяем фильтры панелью массовых действий.
            <>
              <span className="kw-bulk-count">{selectedCards.size} выбрано</span>
              <CardActions
                disabled={bulkBusy}
                boardColumns={boardColumns}
                assignees={cardAssigneeOptions}
                onColumn={bulkCardColumn}
                onAssignee={bulkCardAssignee}
                onDelete={bulkCardDelete}
              />
              <Button variant="ghost" size="sm" onClick={clearCardSelection}>
                <X size={14} /> Снять выбор
              </Button>
            </>
          ) : (
            <>
              <div className="search-field">
                <Search size={15} className="search-icon" />
                <input
                  className="search"
                  placeholder="Поиск по тексту поста…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') reloadBoard()
                  }}
                />
              </div>

              <Popover.Root>
                <Popover.Trigger
                  className={['btn', 'btn-secondary', 'board-filters-btn', activeFilters ? 'has-active' : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <SlidersHorizontal size={15} /> Фильтры
                  {activeFilters > 0 && <span className="filter-badge">{activeFilters}</span>}
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    className="filters-pop"
                    align="start"
                    sideOffset={6}
                    // Клик по выпадашке Select (она в портале) не должен закрывать поповер.
                    onInteractOutside={(e) => {
                      if ((e.target as Element | null)?.closest('[data-radix-popper-content-wrapper]')) {
                        e.preventDefault()
                      }
                    }}
                  >
                    <label className="field">
                      <span>Исполнитель</span>
                      <Select
                        value={assigneeFilter}
                        onValueChange={setAssigneeFilter}
                        aria-label="Фильтр по исполнителю"
                        options={[
                          { value: '', label: 'Все исполнители' },
                          { value: actor.email, label: 'Мои' },
                          ...members
                            .filter((m) => m.status === 'active')
                            .filter((a) => a.email !== actor.email)
                            .map((a) => ({ value: a.email, label: a.email }))
                        ]}
                      />
                    </label>
                    <label className="field">
                      <span>Период</span>
                      <Select
                        value={period}
                        onValueChange={(value) => setPeriod(value as PeriodKey)}
                        aria-label="Период"
                        options={PERIOD_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                      />
                    </label>
                    {languageOptions.length > 1 && (
                      <label className="field">
                        <span>Язык</span>
                        <Select
                          value={languageFilter}
                          onValueChange={setLanguageFilter}
                          aria-label="Язык"
                          options={[{ value: '', label: 'Все языки' }, ...languageOptions]}
                        />
                      </label>
                    )}
                    <button
                      type="button"
                      className={['filters-toggle', favoriteOnly ? 'on' : ''].filter(Boolean).join(' ')}
                      onClick={() => setFavoriteOnly((v) => !v)}
                    >
                      <Star size={15} /> Только избранное
                    </button>
                    {activeFilters > 0 && (
                      <button type="button" className="btn btn-ghost btn-sm filters-reset" onClick={resetFilters}>
                        Сбросить фильтры
                      </button>
                    )}
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>

              {sort !== 'pop_desc' && (
                <Select
                  className="board-sort"
                  value={sort}
                  onValueChange={(value) => setSort(value as SortKey)}
                  aria-label="Сортировка"
                  title={sort === 'manual' ? 'Порядок задаётся перетаскиванием карточек' : undefined}
                  options={SORT_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                />
              )}
              <Button
                variant={sort === 'pop_desc' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setSort((s) => (s === 'pop_desc' ? 'date_desc' : 'pop_desc'))}
                title="Сортировать по вовлечённости — самые залетающие сверху"
              >
                <Flame size={15} /> Залетающие
              </Button>
              {sort === 'pop_desc' && (
                <Select
                  className="board-sort"
                  value={period}
                  onValueChange={(value) => setPeriod(value as PeriodKey)}
                  aria-label="Период залетания"
                  title="За какой период считать залетающие"
                  options={PERIOD_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                />
              )}
            </>
          )}
        </div>

        <Board
          boardColumns={boardColumns}
          columns={columns}
          totals={totals}
          loadingCols={loadingCols}
          keywordById={keywordById}
          manualSort={sort === 'manual'}
          selected={selectedCards}
          onColumnsChange={handleColumnsChange}
          onToggleCard={toggleCard}
          onSetColumnSelection={setColumnSelection}
          onColumnChange={handleColumnChange}
          onLoadMore={(columnId) => loadColumn(columnId, false)}
          onOpen={(postId) => navigate(`/posts/${postId}`, { peekProjectId: projectId })}
          onAddColumn={addColumn}
          onRenameColumn={setEditingColumn}
          onDeleteColumn={setDeletingColumn}
        />
      </main>
        </>
      )}

      {showSettings && (
        <ProjectSettingsModal
          actor={actor}
          project={project}
          members={members}
          onProjectUpdated={setProject}
          onClose={() => setShowSettings(false)}
          onSaved={() => {
            setShowSettings(false)
            loadMeta()
            notify('Проект обновлён')
          }}
          onDeleted={() => {
            notify('Проект удалён')
            navigate('/')
          }}
          onMembersChanged={loadMeta}
          onError={(msg) => notify(msg, 'error')}
        />
      )}

      {editingColumn && (
        <ColumnSettingsModal
          column={editingColumn}
          onClose={() => setEditingColumn(null)}
          onSubmit={(input) => saveColumnSettings(editingColumn.id, input)}
        />
      )}

      {deletingColumn && (
        <DeleteColumnModal
          column={deletingColumn}
          columns={boardColumns}
          total={totals[deletingColumn.id] ?? 0}
          onClose={() => setDeletingColumn(null)}
          onDelete={(moveToColumnId) => deleteColumn(deletingColumn.id, moveToColumnId)}
        />
      )}

      {peekPostId && (
        <PostView
          key={peekPostId}
          variant="peek"
          postId={peekPostId}
          actor={actor}
          onPostChange={applyPostUpdate}
          onClose={() => {
            // Изменения карточки уже применены точечно через onPostChange,
            // поэтому на закрытии доску НЕ перезагружаем — просто возвращаемся.
            navigate(`/projects/${projectId}`)
          }}
        />
      )}
    </div>
  )
}

// Опции автопоиска для слова: «Выключен» (isActive=false) + интервалы в минутах.
const KW_INTERVAL_OPTIONS: { value: string; label: string }[] = [
  { value: 'off', label: 'Автопоиск выключен' },
  { value: '15', label: 'Каждые 15 минут' },
  { value: '30', label: 'Каждые 30 минут' },
  { value: '60', label: 'Каждый час' },
  { value: '180', label: 'Каждые 3 часа' },
  { value: '360', label: 'Каждые 6 часов' },
  { value: '720', label: 'Каждые 12 часов' }
]

// Глубина автопоиска проекта: как далеко в прошлое искать посты. Без «без ограничения»,
// максимум — полгода; значения совпадают с SEARCH_MAX_AGE_DAYS на сервере.
const SEARCH_DEPTH_OPTIONS: { value: string; label: string }[] = [
  { value: '3', label: 'За 3 дня' },
  { value: '7', label: 'За неделю' },
  { value: '14', label: 'За 2 недели' },
  { value: '30', label: 'За месяц' },
  { value: '90', label: 'За квартал' },
  { value: '180', label: 'За полгода' }
]

// Текущее значение автопоиска слова как value селекта.
function keywordIntervalValue(keyword: Keyword): string {
  return keyword.isActive ? String(keyword.autoSearchIntervalMinutes) : 'off'
}

// Подпись частоты под словом в сайдбаре.
function keywordFrequencyLabel(keyword: Keyword): string {
  const value = keywordIntervalValue(keyword)
  return KW_INTERVAL_OPTIONS.find((opt) => opt.value === value)?.label ?? 'Автопоиск выключен'
}

// Подпись «когда последний раз искали» под словом. failed — последний прогон с ошибкой.
function keywordLastRunLabel(keyword: Keyword): { text: string; failed: boolean } {
  if (!keyword.lastRunAt) return { text: 'ещё не искали', failed: false }
  const failed = keyword.lastRunStatus === 'failed'
  return { text: `${failed ? 'ошибка' : 'обновлено'} ${formatRelative(keyword.lastRunAt)}`, failed }
}

// Аккуратные иконки чекбокса: путь симметричен в viewBox, поэтому ровно центрируется.
const CheckTick = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.6 6.3 5 8.7 9.4 3.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
const CheckDash = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M3 6h6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
)

// Меню массовых действий над выбранными словами (частота + удаление).
function BulkActions({
  disabled,
  onInterval,
  onDelete
}: {
  disabled: boolean
  onInterval: (value: string) => void
  onDelete: () => void
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="btn btn-secondary btn-sm kw-actions-btn" disabled={disabled}>
        Действия <ChevronDown size={14} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align="end" sideOffset={6}>
          <DropdownMenu.Label className="menu-label">Частота автопоиска</DropdownMenu.Label>
          {KW_INTERVAL_OPTIONS.map((opt) => (
            <DropdownMenu.Item key={opt.value} className="menu-item" onSelect={() => onInterval(opt.value)}>
              {opt.label}
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="menu-sep" />
          <DropdownMenu.Item className="menu-item menu-item-danger" onSelect={() => onDelete()}>
            <Trash2 size={14} /> Удалить выбранные
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function SidebarKeywords({
  projectId,
  keywords,
  activeKeywordId,
  onFilter,
  onChanged,
  onSearched,
  onSearchedAll,
  onError
}: {
  projectId: string
  keywords: Keyword[]
  activeKeywordId: string
  onFilter: (keywordId: string) => void
  onChanged: () => void
  onSearched: (keyword: Keyword, found: number, created: number) => void
  onSearchedAll: (found: number, created: number, failed: number) => void
  onError: (message: string) => void
}) {
  const [query, setQuery] = useState('')
  const [adding, setAdding] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [refreshingAll, setRefreshingAll] = useState(false)
  const [settingsKw, setSettingsKw] = useState<Keyword | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const { notify } = useToast()
  const confirm = useConfirm()
  const selecting = selected.size > 0

  // Если слова перезагрузились извне (удаление из модалки и т.п.) — выкидываем
  // из выбора пропавшие id, чтобы массовые действия не били по несуществующему.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev
      const live = new Set(keywords.map((k) => k.id))
      const next = new Set([...prev].filter((id) => live.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [keywords])

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const clearSelection = () => setSelected(new Set())
  const selectAll = () => setSelected(new Set(keywords.map((k) => k.id).filter((id): id is string => Boolean(id))))
  const allSelected = keywords.length > 0 && selected.size === keywords.length
  const someSelected = selected.size > 0 && !allSelected
  const toggleAll = () => (allSelected ? clearSelection() : selectAll())

  // Массовая смена частоты автопоиска для выбранных слов.
  async function applyBulkInterval(value: string) {
    const ids = [...selected]
    if (ids.length === 0) return
    const input =
      value === 'off'
        ? { isActive: false }
        : { isActive: true, autoSearchIntervalMinutes: Number(value) }
    setBulkBusy(true)
    try {
      await Promise.all(ids.map((id) => api.updateKeyword(id, input)))
      const label = KW_INTERVAL_OPTIONS.find((o) => o.value === value)?.label ?? 'Обновлено'
      notify(`Частота обновлена · ${label}`)
      clearSelection()
      onChanged()
    } catch (err) {
      onError(errorText(err))
    } finally {
      setBulkBusy(false)
    }
  }

  // Массовое удаление выбранных слов (посты остаются).
  async function bulkDelete() {
    const ids = [...selected]
    if (ids.length === 0) return
    const ok = await confirm({
      title: 'Удалить выбранные слова?',
      message: `Будет удалено: ${ids.length}. Найденные посты останутся.`
    })
    if (!ok) return
    setBulkBusy(true)
    try {
      await Promise.all(ids.map((id) => api.deleteKeyword(id)))
      if (activeKeywordId && selected.has(activeKeywordId)) onFilter(activeKeywordId) // снимет фильтр
      clearSelection()
      notify('Слова удалены')
      onChanged()
    } catch (err) {
      onError(errorText(err))
    } finally {
      setBulkBusy(false)
    }
  }

  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!query.trim()) return
    setAdding(true)
    try {
      await api.createKeyword(projectId, { query: query.trim() })
      setQuery('')
      onChanged()
    } catch (err) {
      onError(errorText(err))
    } finally {
      setAdding(false)
    }
  }

  async function runSearch(keyword: Keyword) {
    if (!keyword.id) return
    setBusyId(keyword.id)
    try {
      const res = await api.searchKeyword(keyword.id)
      onSearched(keyword, res.foundCount, res.newCount)
    } catch (err) {
      onError(errorText(err))
    } finally {
      setBusyId(null)
    }
  }

  // Однократный поиск по всем словам подряд (вне расписания автопоиска).
  async function runAllSearch() {
    const ids = keywords.map((k) => k.id).filter((id): id is string => Boolean(id))
    if (ids.length === 0 || refreshingAll) return
    setRefreshingAll(true)
    let found = 0
    let created = 0
    let failed = 0
    for (const id of ids) {
      try {
        const res = await api.searchKeyword(id)
        found += res.foundCount
        created += res.newCount
      } catch {
        failed += 1
      }
    }
    setRefreshingAll(false)
    onChanged() // подтянуть свежие lastRunAt у слов
    onSearchedAll(found, created, failed)
  }

  return (
    <section className="stack" style={{ gap: 10 }}>
      <form className="kw-add" onSubmit={add}>
        <input
          className="grow"
          placeholder="Новый запрос…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <IconButton label="Добавить" type="submit" tone="ok" disabled={adding || !query.trim()}>
          <Plus size={16} />
        </IconButton>
      </form>
      <div className="kw-head">
        <button
          type="button"
          className={['kw-check', 'kw-check-all', allSelected ? 'on' : someSelected ? 'partial' : '']
            .filter(Boolean)
            .join(' ')}
          aria-label={selected.size > 0 ? 'Снять выбор' : 'Выбрать все'}
          onClick={toggleAll}
          disabled={keywords.length === 0}
        >
          {allSelected ? <CheckTick /> : someSelected ? <CheckDash /> : null}
        </button>
        {selecting ? (
          <>
            <span className="kw-bulk-count">{selected.size} выбрано</span>
            <div className="kw-head-actions">
              <BulkActions disabled={bulkBusy} onInterval={applyBulkInterval} onDelete={bulkDelete} />
              <IconButton label="Снять выбор" onClick={clearSelection}>
                <X size={15} />
              </IconButton>
            </div>
          </>
        ) : (
          <>
            <span className="section-label kw-head-label">Ключевые слова</span>
            {keywords.length > 0 && (
              <IconButton
                label="Обновить все"
                tone="ok"
                disabled={refreshingAll}
                onClick={runAllSearch}
              >
                <RefreshCw size={14} className={refreshingAll ? 'spin' : ''} />
              </IconButton>
            )}
          </>
        )}
      </div>

      {keywords.length === 0 ? (
        <div className="faint" style={{ fontSize: 'var(--fs-13)' }}>
          Нет ключевых слов. Добавьте первый запрос выше.
        </div>
      ) : (
        <div className={['kw-items', selecting ? 'selecting' : ''].filter(Boolean).join(' ')}>
          {keywords.map((kw) => (
            <div
              key={kw.id}
              className={[
                'kw-item',
                activeKeywordId === kw.id ? 'active' : '',
                busyId === kw.id ? 'searching' : '',
                kw.id && selected.has(kw.id) ? 'selected' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                if (!kw.id) return
                if (selecting) toggleSelect(kw.id)
                else onFilter(kw.id)
              }}
              title={kw.lastRunAt ? `Последний поиск: ${formatDateTime(kw.lastRunAt)}` : 'Ещё не искали'}
            >
              <button
                type="button"
                className={['kw-check', kw.id && selected.has(kw.id) ? 'on' : ''].filter(Boolean).join(' ')}
                aria-label={kw.id && selected.has(kw.id) ? 'Снять выбор' : 'Выбрать'}
                onClick={(e) => {
                  e.stopPropagation()
                  if (kw.id) toggleSelect(kw.id)
                }}
              >
                {kw.id && selected.has(kw.id) ? <CheckTick /> : null}
              </button>
              <div className="kw-item-text">
                <span className="kw-item-q">{kw.query}</span>
                <span className="kw-item-sub">
                  {kw.isActive && <>{keywordFrequencyLabel(kw)}{' · '}</>}
                  <span className={keywordLastRunLabel(kw).failed ? 'kw-item-failed' : undefined}>
                    {keywordLastRunLabel(kw).text}
                  </span>
                </span>
              </div>
              {!selecting && (
                <span className="kw-item-actions">
                  <IconButton
                    label="Искать сейчас"
                    tone="ok"
                    className="kw-search-btn"
                    disabled={busyId === kw.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      runSearch(kw)
                    }}
                  >
                    <RefreshCw size={14} className={busyId === kw.id ? 'spin' : ''} />
                  </IconButton>
                  <IconButton
                    label="Настройки слова"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSettingsKw(kw)
                    }}
                  >
                    <Settings size={14} />
                  </IconButton>
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {settingsKw && (
        <KeywordSettingsModal
          keyword={settingsKw}
          onClose={() => setSettingsKw(null)}
          onSaved={() => {
            setSettingsKw(null)
            onChanged()
          }}
          onDeleted={(deletedId) => {
            if (activeKeywordId === deletedId) onFilter(deletedId) // снимет фильтр
            setSettingsKw(null)
            onChanged()
          }}
          onError={onError}
        />
      )}
    </section>
  )
}

function KeywordSettingsModal({
  keyword,
  onClose,
  onSaved,
  onDeleted,
  onError
}: {
  keyword: Keyword
  onClose: () => void
  onSaved: () => void
  onDeleted: (keywordId: string) => void
  onError: (message: string) => void
}) {
  const [name, setName] = useState(keyword.query)
  const [interval, setInterval] = useState(keywordIntervalValue(keyword))
  const [saving, setSaving] = useState(false)
  const confirm = useConfirm()

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim() || !keyword.id) return
    setSaving(true)
    try {
      const input =
        interval === 'off'
          ? { query: name.trim(), isActive: false }
          : { query: name.trim(), isActive: true, autoSearchIntervalMinutes: Number(interval) }
      await api.updateKeyword(keyword.id, input)
      onSaved()
    } catch (err) {
      onError(errorText(err))
      setSaving(false)
    }
  }

  async function remove() {
    if (!keyword.id) return
    const ok = await confirm({
      title: 'Удалить ключевое слово?',
      message: `«${keyword.query}» — найденные посты останутся.`
    })
    if (!ok) return
    try {
      await api.deleteKeyword(keyword.id)
      onDeleted(keyword.id)
    } catch (err) {
      onError(errorText(err))
    }
  }

  return (
    <Modal title="Настройки слова" onClose={onClose}>
      <form className="stack" onSubmit={save}>
        <Field label="Запрос">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Автопоиск" hint="Как часто автоматически искать новые посты по этому слову.">
          <Select value={interval} onValueChange={setInterval} options={KW_INTERVAL_OPTIONS} />
        </Field>
        <div className="row spread">
          <Button type="button" variant="danger" onClick={remove}>
            <Trash2 size={14} /> Удалить
          </Button>
          <div className="row">
            <Button type="button" variant="secondary" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

// Меню массовых действий над выбранными карточками: колонка / ответственный (подменю) + удаление.
function CardActions({
  disabled,
  boardColumns,
  assignees,
  onColumn,
  onAssignee,
  onDelete
}: {
  disabled: boolean
  boardColumns: BoardColumn[]
  assignees: { value: string; label: string }[]
  onColumn: (columnId: string) => void
  onAssignee: (email: string) => void
  onDelete: () => void
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="btn btn-secondary btn-sm kw-actions-btn" disabled={disabled}>
        Действия <ChevronDown size={14} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align="end" sideOffset={6}>
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className="menu-item menu-subtrigger">
              <span>Сменить колонку</span>
              <ChevronRight size={14} />
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent className="menu" sideOffset={4} alignOffset={-4}>
                {boardColumns.map((column) => (
                  <DropdownMenu.Item key={column.id} className="menu-item" onSelect={() => onColumn(column.id)}>
                    {column.name}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className="menu-item menu-subtrigger">
              <span>Сменить ответственного</span>
              <ChevronRight size={14} />
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent className="menu" sideOffset={4} alignOffset={-4}>
                {assignees.map((opt) => (
                  <DropdownMenu.Item key={opt.value || 'none'} className="menu-item" onSelect={() => onAssignee(opt.value)}>
                    {opt.label}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>
          <DropdownMenu.Separator className="menu-sep" />
          <DropdownMenu.Item className="menu-item menu-item-danger" onSelect={() => onDelete()}>
            <Trash2 size={14} /> Удалить
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function Board({
  boardColumns,
  columns,
  totals,
  loadingCols,
  keywordById,
  manualSort,
  selected,
  onColumnsChange,
  onToggleCard,
  onSetColumnSelection,
  onColumnChange,
  onLoadMore,
  onOpen,
  onAddColumn,
  onRenameColumn,
  onDeleteColumn
}: {
  boardColumns: BoardColumn[]
  columns: Columns
  totals: Record<string, number>
  loadingCols: Set<string>
  keywordById: Map<string | null, string>
  manualSort: boolean
  selected: Set<string>
  onColumnsChange: (columns: BoardColumn[]) => void
  onToggleCard: (id: string) => void
  onSetColumnSelection: (ids: string[], on: boolean) => void
  onColumnChange: (columnId: string, nextList: BoardPost[]) => void
  onLoadMore: (columnId: string) => void
  onOpen: (postId: string) => void
  onAddColumn: (name: string) => Promise<void>
  onRenameColumn: (column: BoardColumn) => void
  onDeleteColumn: (column: BoardColumn) => void
}) {
  return (
    <div className="board">
      <ReactSortable<BoardColumn>
        tag="div"
        className="board-cols"
        list={boardColumns}
        setList={onColumnsChange}
        animation={150}
        handle=".board-col-drag"
        ghostClass="column-ghost"
      >
        {boardColumns.map((boardColumn) => {
        const column = columns[boardColumn.id] || []
        const total = totals[boardColumn.id] ?? 0
        const hasMore = column.length < total
        const loading = loadingCols.has(boardColumn.id)
        const ids = column.map((post) => post.id)
        const selectedIds = ids.filter((id) => selected.has(id))
        const allSel = ids.length > 0 && selectedIds.length === ids.length
        const someSel = selectedIds.length > 0 && !allSel
        const selecting = selectedIds.length > 0
        return (
          <div key={boardColumn.id} className={`board-col board-col-color-${boardColumn.color || 'gray'}`}>
            <div className="board-col-head">
              <button type="button" className="board-col-drag" aria-label="Перетащить колонку">
                <GripVertical size={14} />
              </button>
              {ids.length > 0 && (
                <button
                  type="button"
                  className={['kw-check', 'board-col-check', allSel ? 'on' : someSel ? 'partial' : '']
                    .filter(Boolean)
                    .join(' ')}
                  aria-label={selecting ? 'Снять выбор' : 'Выбрать все в колонке'}
                  onClick={() => onSetColumnSelection(ids, !allSel)}
                >
                  {allSel ? <CheckTick /> : someSel ? <CheckDash /> : null}
                </button>
              )}
              <span className="dot" />
              <span className="board-col-title">{boardColumn.name}</span>
              <span className="board-col-count">{total}</span>
              <ColumnActions
                column={boardColumn}
                canDelete={boardColumns.length > 1}
                onRename={onRenameColumn}
                onDelete={onDeleteColumn}
              />
            </div>
            <ReactSortable<BoardPost>
              tag="div"
              className="board-col-list"
              group="board"
              animation={150}
              ghostClass="card-ghost"
              // Чекбокс выбора не должен инициировать перетаскивание карточки.
              filter=".post-card-check"
              preventOnFilter={false}
              // sort=false в режиме сортировки: внутри колонки переупорядочивать нельзя,
              // но перенос между колонками (смена статуса) остаётся доступным.
              sort={manualSort}
              list={column}
              setList={(next) => onColumnChange(boardColumn.id, next)}
            >
              {column.map((post) => (
                <div key={post.id} data-id={post.id} className="board-card-wrap">
                  <PostCard
                    post={post}
                    keywordName={keywordById.get(post.keywordId) || ''}
                    onOpen={onOpen}
                    selected={selected.has(post.id)}
                    onToggleSelect={() => onToggleCard(post.id)}
                  />
                </div>
              ))}
            </ReactSortable>
            {hasMore && (
              <button
                type="button"
                className="board-col-more"
                disabled={loading}
                onClick={() => onLoadMore(boardColumn.id)}
              >
                {loading ? 'Загрузка…' : `Загрузить ещё (${total - column.length})`}
              </button>
            )}
          </div>
        )
        })}
      </ReactSortable>
      <AddColumnCard onAdd={onAddColumn} />
    </div>
  )
}

function ColumnActions({
  column,
  canDelete,
  onRename,
  onDelete
}: {
  column: BoardColumn
  canDelete: boolean
  onRename: (column: BoardColumn) => void
  onDelete: (column: BoardColumn) => void
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="board-col-menu" aria-label="Действия с колонкой">
        <MoreHorizontal size={15} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align="end" sideOffset={6}>
          <DropdownMenu.Item className="menu-item" onSelect={() => onRename(column)}>
            Настроить
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="menu-sep" />
          <DropdownMenu.Item
            className="menu-item menu-item-danger"
            disabled={!canDelete}
            onSelect={() => onDelete(column)}
          >
            <Trash2 size={14} /> Удалить
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function AddColumnCard({ onAdd }: { onAdd: (name: string) => Promise<void> }) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    try {
      await onAdd(name.trim())
      setName('')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="board-add-col" onSubmit={submit}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Новая колонка" />
      <IconButton label="Добавить колонку" type="submit" tone="ok" disabled={saving || !name.trim()}>
        <Plus size={16} />
      </IconButton>
    </form>
  )
}

function ColumnSettingsModal({
  column,
  onClose,
  onSubmit
}: {
  column: BoardColumn
  onClose: () => void
  onSubmit: (input: { name: string; color: BoardColumnColor }) => Promise<void>
}) {
  const [name, setName] = useState(column.name)
  const [color, setColor] = useState<BoardColumnColor>(column.color || 'gray')
  const [saving, setSaving] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    try {
      await onSubmit({ name: name.trim(), color })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Настройки колонки" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <Field label="Название">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Цвет">
          <div className="column-color-grid">
            {BOARD_COLUMN_COLORS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={[
                  'column-color-swatch',
                  `board-col-color-${option.value}`,
                  color === option.value ? 'selected' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={option.label}
                title={option.label}
                onClick={() => setColor(option.value)}
              >
                <span className="column-color-dot" />
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </Field>
        <div className="row spread">
          <span />
          <div className="row">
            <Button type="button" variant="secondary" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

function DeleteColumnModal({
  column,
  columns,
  total,
  onClose,
  onDelete
}: {
  column: BoardColumn
  columns: BoardColumn[]
  total: number
  onClose: () => void
  onDelete: (moveToColumnId?: string) => Promise<void>
}) {
  const targets = columns.filter((item) => item.id !== column.id)
  const [moveToColumnId, setMoveToColumnId] = useState(targets[0]?.id ?? '')
  const [saving, setSaving] = useState(false)
  const hasPosts = total > 0

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (hasPosts && !moveToColumnId) return
    setSaving(true)
    try {
      await onDelete(hasPosts ? moveToColumnId : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Удалить колонку" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <Notice>
          Колонка «{column.name}» будет удалена.
          {hasPosts ? ` Посты внутри неё (${total}) нужно перенести в другую колонку.` : ' В ней нет постов.'}
        </Notice>
        {hasPosts && (
          <Field label="Перенести посты в">
            <Select
              value={moveToColumnId}
              onValueChange={setMoveToColumnId}
              options={targets.map((target) => ({ value: target.id, label: target.name }))}
            />
          </Field>
        )}
        <div className="row spread">
          <span />
          <div className="row">
            <Button type="button" variant="secondary" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" variant="danger" disabled={saving || (hasPosts && !moveToColumnId)}>
              {saving ? 'Удаляем…' : 'Удалить'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

function PostCard({
  post,
  keywordName,
  onOpen,
  selected,
  onToggleSelect
}: {
  post: BoardPost
  keywordName: string
  onOpen: (postId: string) => void
  selected: boolean
  onToggleSelect: () => void
}) {
  return (
    <div
      className={['post-card', selected ? 'selected' : ''].filter(Boolean).join(' ')}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(post.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(post.id)
      }}
    >
      <button
        type="button"
        className={['kw-check', 'post-card-check', selected ? 'on' : ''].filter(Boolean).join(' ')}
        aria-label={selected ? 'Снять выбор' : 'Выбрать карточку'}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          onToggleSelect()
        }}
      >
        {selected ? <CheckTick /> : null}
      </button>
      <div className="post-card-head">
        <span className="post-card-author">@{post.authorUsername}</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {post.assignedToEmail && <Avatar email={post.assignedToEmail} size={18} />}
          {post.isFavorite && <Star size={13} style={{ color: 'var(--amber)' }} fill="currentColor" />}
        </span>
      </div>
      <div className="post-card-text">{post.text || '(без текста)'}</div>
      {keywordName && (
        <span className="post-card-kw" title={`Отобран по ключевику: ${keywordName}`}>
          <Tag size={11} /> {keywordName}
        </span>
      )}
      <div className="post-card-meta">
        <span>{post.publishedAt ? formatRelative(post.publishedAt) : formatRelative(post.foundAt)}</span>
        <span className="metrics">
          {post.commentCount > 0 && (
            <span className="metric post-card-comments" title={`Обсуждение: ${post.commentCount}`}>
              <MessagesSquare size={12} /> {post.commentCount}
            </span>
          )}
          {post.metrics.likes != null && (
            <span className="metric">
              <Heart size={12} /> {formatCount(post.metrics.likes)}
            </span>
          )}
          {post.metrics.replies != null && (
            <span className="metric">
              <MessageSquare size={12} /> {formatCount(post.metrics.replies)}
            </span>
          )}
          {post.metrics.reposts != null && (
            <span className="metric">
              <Repeat2 size={12} /> {formatCount(post.metrics.reposts)}
            </span>
          )}
        </span>
      </div>
    </div>
  )
}

function ProjectSettingsModal({
  actor,
  project,
  members,
  onClose,
  onProjectUpdated,
  onSaved,
  onDeleted,
  onMembersChanged,
  onError
}: {
  actor: Actor
  project: Project
  members: ProjectMember[]
  onClose: () => void
  onProjectUpdated: (project: Project) => void
  onSaved: () => void
  onDeleted: () => void
  onMembersChanged: () => void
  onError: (message: string) => void
}) {
  const [tab, setTab] = useState<'general' | 'templates' | 'members'>('general')
  const [name, setName] = useState(project.name)
  const [description, setDescription] = useState(project.description)
  const [searchDepth, setSearchDepth] = useState(String(project.searchMaxAgeDays))
  const [replyExamples, setReplyExamples] = useState(project.replyExamples)
  const [inviteEmail, setInviteEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [inviting, setInviting] = useState(false)
  const confirm = useConfirm()

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim() || !project.id) return
    setSaving(true)
    try {
      await api.updateProject(project.id, {
        ...(project.canRenameProject ? { name: name.trim() } : {}),
        description: description.trim(),
        searchMaxAgeDays: Number(searchDepth)
      })
      onSaved()
    } catch (err) {
      onError(errorText(err))
      setSaving(false)
    }
  }

  async function remove() {
    if (!project.id) return
    const ok = await confirm({
      title: `Удалить проект «${project.name}»?`,
      message: 'Будут удалены все ключевые слова и посты. Действие необратимо.'
    })
    if (!ok) return
    try {
      await api.deleteProject(project.id)
      onDeleted()
    } catch (err) {
      onError(errorText(err))
    }
  }

  async function invite() {
    if (!project.id || !inviteEmail.trim()) return
    setInviting(true)
    try {
      await api.inviteProjectMember(project.id, inviteEmail.trim())
      setInviteEmail('')
      onMembersChanged()
    } catch (err) {
      onError(errorText(err))
    } finally {
      setInviting(false)
    }
  }

  async function removeMember(member: ProjectMember) {
    if (!project.id) return
    const ok = await confirm({ title: 'Удалить участника?', message: `${member.email} потеряет доступ к проекту.` })
    if (!ok) return
    try {
      await api.removeProjectMember(project.id, member.email)
      onMembersChanged()
    } catch (err) {
      onError(errorText(err))
    }
  }

  return (
    <Modal title="Настройки проекта" onClose={onClose} className="modal-wide">
      <div className="stack" style={{ gap: 16 }}>
        <div className="tabs" role="tablist">
          {(
            [
              ['general', 'Основное'],
              ['templates', 'Шаблоны ответов'],
              ['members', 'Участники']
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={['tab', tab === value ? 'active' : ''].filter(Boolean).join(' ')}
              onClick={() => setTab(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'general' && (
          <form className="stack" onSubmit={save}>
            <Field label="Название">
              {project.canRenameProject ? (
                <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
              ) : (
                <Input value={name} disabled />
              )}
            </Field>
            <Field
              label="Описание проекта"
              hint="Коротко о чём проект — ИИ учитывает это при генерации. Стиль ответов задаётся примерами на вкладке «Шаблоны ответов»."
            >
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field
              label="Глубина автопоиска"
              hint="Насколько далеко в прошлое искать посты при автопоиске. Меньший период — быстрее и экономнее."
            >
              <Select value={searchDepth} onValueChange={setSearchDepth} aria-label="Глубина автопоиска" options={SEARCH_DEPTH_OPTIONS} />
            </Field>
            <div className="row spread">
              {project.canDeleteProject ? (
                <Button type="button" variant="danger" onClick={remove}>
                  <Trash2 size={14} /> Удалить проект
                </Button>
              ) : (
                <span className="faint" style={{ fontSize: 'var(--fs-12)' }}>
                  Вы приглашены в проект. Переименование и удаление доступны владельцу.
                </span>
              )}
              <div className="row">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Отмена
                </Button>
                <Button type="submit" disabled={saving || !name.trim()}>
                  {saving ? 'Сохраняем…' : 'Сохранить'}
                </Button>
              </div>
            </div>
          </form>
        )}

        {tab === 'templates' && project.id && (
          <TemplatesSettings
            projectId={project.id}
            examples={replyExamples}
            onProjectSaved={(updatedProject) => {
              setReplyExamples(updatedProject.replyExamples)
              onProjectUpdated(updatedProject)
            }}
          />
        )}

        {tab === 'members' && (
          <ProjectMembersBlock
            actor={actor}
            project={project}
            members={members}
            inviteEmail={inviteEmail}
            inviting={inviting}
            onInviteEmail={setInviteEmail}
            onInvite={invite}
            onRemove={removeMember}
          />
        )}
      </div>
    </Modal>
  )
}

function ProjectMembersBlock({
  actor,
  project,
  members,
  inviteEmail,
  inviting,
  onInviteEmail,
  onInvite,
  onRemove
}: {
  actor: Actor
  project: Project
  members: ProjectMember[]
  inviteEmail: string
  inviting: boolean
  onInviteEmail: (value: string) => void
  onInvite: () => void
  onRemove: (member: ProjectMember) => void
}) {
  return (
    <section className="stack" style={{ gap: 10 }}>
      <div className="section-label">Участники</div>
      {project.canManageMembers && (
        <div className="row">
          <Input
            className="grow"
            type="email"
            placeholder="teammate@example.com"
            value={inviteEmail}
            onChange={(e) => onInviteEmail(e.target.value)}
          />
          <Button type="button" disabled={inviting || !inviteEmail.trim()} onClick={onInvite}>
            <UserPlus size={14} /> Пригласить
          </Button>
        </div>
      )}
      <div>
        {members.map((member) => (
          <div key={member.email} className="admin-row">
            <Avatar email={member.email} size={30} />
            <div className="admin-row-main">
              <div className="admin-row-email">{member.email}</div>
              <div className="faint" style={{ fontSize: 'var(--fs-12)' }}>
                {member.email === actor.email ? 'это вы' : member.status === 'active' ? 'активный доступ' : 'приглашение отправлено'}
              </div>
            </div>
            <Badge tone={member.role === 'owner' ? 'active' : 'invited'}>
              {member.role === 'owner' ? 'владелец' : member.status === 'active' ? 'участник' : 'приглашён'}
            </Badge>
            {project.canManageMembers && member.canRemove && (
              <IconButton label="Удалить из проекта" tone="danger" onClick={() => onRemove(member)}>
                <Trash2 size={15} />
              </IconButton>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
